import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { isFalsy } from 'utility-types';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { ComprobantePdf, ComprobantePdfService } from '@services/comprobantes/comprobante-pdf.service';

export interface DatosCompartirComprobante {
  idComprobante: number;
  nroComprobante: string;
  /** Texto que acompaña al PDF (propiedad, propietario, etc.) */
  descripcion?: string;
}

/**
 * Opciones para compartir el PDF de un comprobante.
 *
 * El camino bueno es el compartir nativo del sistema (navigator.share con
 * archivos): abre la bandeja del SO y desde ahí va a WhatsApp, Telegram, Drive,
 * correo o lo que el usuario tenga instalado, con el PDF adjunto de verdad.
 *
 * Ese API pide contexto seguro (https o localhost) y no está en todos los
 * navegadores —Firefox no comparte archivos—, así que hay alternativas:
 * WhatsApp y correo abren con el mensaje ya escrito, pero el PDF hay que
 * adjuntarlo a mano porque ninguna URL permite adjuntar archivos. Por eso, en
 * esos casos, primero se descarga el PDF.
 */
@Component({
  selector: 'app-dialog-compartir-comprobante',
  templateUrl: './dialog-compartir-comprobante.component.html',
  styleUrls: ['./dialog-compartir-comprobante.component.css']
})
export class DialogCompartirComprobanteComponent implements OnInit {

  pdf: ComprobantePdf | null = null;
  cargando = true;
  error = "";

  /** El navegador soporta compartir archivos */
  soportaCompartirNativo = false;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DatosCompartirComprobante,
    private dialogRef: MatDialogRef<DialogCompartirComprobanteComponent>,
    private comprobantePdfService: ComprobantePdfService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.soportaCompartirNativo = this.detectarSoporteCompartir();
    this.cargarPdf();
  }

  /**
   * navigator.canShare({files}) es la única forma confiable de saber si el
   * navegador comparte archivos. Chequear solo navigator.share da falsos
   * positivos: hay navegadores que comparten texto pero no archivos.
   */
  private detectarSoporteCompartir(): boolean {
    try {
      const nav: any = window.navigator;

      if (!nav || typeof nav.share !== 'function' || typeof nav.canShare !== 'function')
        return false;

      const archivoPrueba = new File([new Uint8Array([1])], 'prueba.pdf', { type: 'application/pdf' });

      return nav.canShare({ files: [archivoPrueba] });
    } catch {
      return false;
    }
  }

  private cargarPdf() {
    this.cargando = true;
    this.spinner.show("spCompartir");

    this.comprobantePdfService.obtenerPdf(this.data.idComprobante).subscribe(
      pdf => {
        this.pdf = pdf;
        this.cargando = false;
        this.spinner.hide("spCompartir");
      },
      err => {
        this.cargando = false;
        this.error = "No se pudo obtener el PDF de este comprobante.";
        this.spinner.hide("spCompartir");
      }
    );
  }

  get mensaje(): string {
    const descripcion = isFalsy(this.data.descripcion) ? "" : ` - ${this.data.descripcion}`;

    return `Comprobante Nro. ${this.data.nroComprobante}${descripcion}`;
  }

  /** Bandeja nativa del sistema, con el PDF adjunto */
  async compartirNativo() {
    if (!this.pdf)
      return;

    try {
      const archivo = this.comprobantePdfService.base64AFile(this.pdf.contenidoBase64, this.pdf.nombreArchivo);
      const nav: any = window.navigator;

      await nav.share({
        title: this.mensaje,
        text: this.mensaje,
        files: [archivo]
      });

      this.dialogRef.close();
    } catch (e: any) {
      // El usuario cancelando la bandeja no es un error que valga avisar
      if (e && e.name === 'AbortError')
        return;

      this.snackBar.showError("No se pudo compartir el archivo.", "Error");
    }
  }

  /**
   * WhatsApp con el mensaje escrito. El PDF se descarga primero porque
   * wa.me no admite adjuntar archivos por URL: hay que arrastrarlo o adjuntarlo.
   */
  compartirWhatsApp() {
    this.descargar();
    window.open(`https://wa.me/?text=${encodeURIComponent(this.mensaje)}`, '_blank');
  }

  /** Cliente de correo con asunto y cuerpo listos. El PDF va aparte. */
  compartirCorreo() {
    this.descargar();

    const asunto = encodeURIComponent(this.mensaje);
    const cuerpo = encodeURIComponent(`${this.mensaje}\n\nSe adjunta el comprobante en PDF.`);

    window.location.href = `mailto:?subject=${asunto}&body=${cuerpo}`;
  }

  descargar() {
    if (!this.pdf)
      return;

    const link = document.createElement("a");
    link.href = "data:application/pdf;base64," + this.pdf.contenidoBase64;
    link.download = this.pdf.nombreArchivo;
    link.click();
  }

  /** Abre el PDF en otra pestaña, para revisarlo antes de mandarlo */
  abrirEnPestania() {
    if (!this.pdf)
      return;

    const archivo = this.comprobantePdfService.base64AFile(this.pdf.contenidoBase64, this.pdf.nombreArchivo);
    const url = URL.createObjectURL(archivo);

    window.open(url, '_blank');

    // Se libera después de que el navegador alcanzó a abrirlo
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  async copiarTexto() {
    try {
      await navigator.clipboard.writeText(this.mensaje);
      this.snackBar.showSuccess("Texto copiado.", "Exito");
    } catch {
      this.snackBar.showError("No se pudo copiar el texto.", "Error");
    }
  }

  cerrar() {
    this.dialogRef.close();
  }
}
